// POST /api/datasets/feature-distribution client.
//
// Backend contract: dev-0.7.21 (combined recovery; dev-0.7.18 introduced the
// endpoint, dev-0.7.19/20 were skipped, dev-0.7.21 fixed a zero-counts bug).
// Full spec in: FederateX_agent_sync/Backend_To_Frontend/
//   2026-06-05_backend_feature_distribution_endpoint_integration_spec.md
//
// Response shape (happy path 200):
//   { dataset_ref, dataset_type, generated_at, sites:[], features:[], warnings:[], limits:{} }
// Error shape (HTTP 400, normalized into a thrown Error here):
//   { error: { code, message, details } }
//
// 5-min in-memory cache keyed by (dataset_ref + sorted sites + params).

import { API_BASE } from '../config/environment';
import { authenticatedFetch } from './api';

const ENDPOINT = '/datasets/feature-distribution';
const CACHE_TTL_MS = 5 * 60 * 1000;

// { key: { ts, value } }
const _cache = new Map();

const cacheKey = (body) => {
  const sortedSites = (body.sites || [])
    .map((s) => `${s.site_id}|${s.capability_id || ''}`)
    .slice()
    .sort()
    .join(',');
  const features = (body.features || []).slice().sort().join(',');
  return JSON.stringify({
    d: body.dataset_ref,
    s: sortedSites,
    f: features,
    il: body.include_label,
    nb: body.numeric_bins,
    tk: body.top_k_categories,
    ss: body.suppress_small_cells,
    mr: body.max_rows_per_site,
    bs: body.bin_strategy, // dev-0.7.23: "round" vs "equal_width" produce different bin_edges
  });
};

// Map a 400 error body to a user-facing string. Spec §6.1 — extend if backend
// adds codes.
export const FRIENDLY_ERROR = {
  MISSING_DATASET_REF: "Please pick a dataset before comparing distributions.",
  INVALID_SITES: "Pick at least one site to compare.",
  TOO_MANY_SITES: "Pick at most 5 sites for distribution comparison.",
  INVALID_PARAM: "Distribution parameters are out of range; please refresh.",
  UNKNOWN_DATASET_REF: "Dataset not available.",
  SITES_DATASET_REF_MISMATCH: "One of the selected sites is not part of this dataset.",
  CAPABILITY_NOT_FOUND: "Selected site is not registered.",
  SITE_NOT_FOUND: "Selected site is not registered.",
  CAPABILITY_SITE_MISMATCH: "Site/capability mismatch, please reselect.",
  INTERNAL_ERROR: "Could not load distributions. Please retry; check Network tab if it persists.",
};

const friendlyMessageFor = (code, fallback) =>
  (code && FRIENDLY_ERROR[code]) ||
  fallback ||
  "Could not load distributions. Please retry.";

/**
 * Fetch feature distribution for (dataset_ref, sites) selection.
 * Returns the full response object on 200, or throws an Error whose
 * `.code` (when present) is the backend error code, `.message` is a
 * UX-suitable string. Caller can inspect err.code for code-specific UI.
 *
 * Honors a 5-min in-memory cache so toggling UI checkboxes that produce
 * the same key skip the network call. Cache is keyed only on the request
 * body shape — it does NOT depend on the Bearer token.
 *
 * Cancellation: pass `signal` from AbortController.signal to abort an
 * in-flight request (e.g. when the user changes sites before the prior
 * fetch completes). On abort, throws a DOMException with name='AbortError'.
 */
export const fetchFeatureDistribution = async (body, { signal } = {}) => {
  if (!body || typeof body !== 'object') {
    const err = new Error('fetchFeatureDistribution requires a request body');
    err.code = 'CLIENT_BAD_REQUEST';
    throw err;
  }
  const key = cacheKey(body);
  const cached = _cache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.value;
  }
  let response;
  try {
    response = await authenticatedFetch(`${API_BASE}${ENDPOINT}`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  } catch (netErr) {
    if (netErr && netErr.name === 'AbortError') throw netErr;
    const err = new Error(friendlyMessageFor(null, 'Network error while loading distributions.'));
    err.code = 'NETWORK';
    err.cause = netErr;
    throw err;
  }
  let payload;
  try {
    payload = await response.json();
  } catch (parseErr) {
    const err = new Error('Server returned an unexpected response. Please retry.');
    err.code = 'BAD_JSON';
    throw err;
  }
  if (!response.ok) {
    const code = payload && payload.error && payload.error.code;
    const backendMsg = payload && payload.error && payload.error.message;
    const err = new Error(friendlyMessageFor(code, backendMsg));
    err.code = code || 'INTERNAL_ERROR';
    err.status = response.status;
    err.details = payload && payload.error && payload.error.details;
    throw err;
  }
  // Demo-only rename for site_smh_01 — see AppContext.fetchSites for the
  // original rationale. Mirrored here because feature-distribution
  // response carries its own `display_name` echoes inside both `sites[]`
  // and `features[].distributions[]`, neither re-looked-up by FE.
  if (payload && typeof payload === 'object') {
    const renameIfSmh = (o) =>
      o && o.site_id === 'site_smh_01'
        ? { ...o, display_name: 'South Metro Hospital' }
        : o;
    if (Array.isArray(payload.sites)) {
      payload.sites = payload.sites.map(renameIfSmh);
    }
    if (Array.isArray(payload.features)) {
      payload.features = payload.features.map((f) =>
        f && Array.isArray(f.distributions)
          ? { ...f, distributions: f.distributions.map(renameIfSmh) }
          : f
      );
    }
  }
  _cache.set(key, { ts: Date.now(), value: payload });
  return payload;
};

// Suggested per-site color map (spec §5.4). Components fall back to a
// hash-stable grey for any site_id not in this table.
export const SITE_COLORS = {
  site_tgh_01: '#3b82f6',   // blue
  site_sun_01: '#10b981',   // green
  site_mtl_01: '#f59e0b',   // amber
  site_oicr_01: '#ef4444',  // red
  site_bccrc_01: '#8b5cf6', // violet
  site_smh_01: '#06b6d4',   // cyan
};

const FALLBACK_COLORS = ['#64748b', '#0f766e', '#7c2d12', '#3730a3', '#9f1239', '#374151'];
export const colorForSite = (siteId, index = 0) =>
  SITE_COLORS[siteId] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

// Resolve the dataset_ref to query, given the user's current site selection
// + the capability registry. Returns null when selection isn't complete or
// sites point at multiple dataset_refs (shouldn't happen but we guard).
export const resolveDatasetRef = (selectedSites, siteCapabilities) => {
  const sites = (selectedSites || []).filter((s) => s && s.site_id && s.capability_id);
  if (sites.length === 0) return null;
  const refs = new Set();
  for (const s of sites) {
    const caps = (siteCapabilities && siteCapabilities[s.site_id]) || [];
    const cap = caps.find((c) => c.capability_id === s.capability_id);
    if (cap && cap.dataset_ref) refs.add(cap.dataset_ref);
  }
  if (refs.size !== 1) return null;
  return [...refs][0];
};

// Reset the cache (mostly for tests / dev tooling). No-op in production paths.
export const _clearFeatureDistributionCache = () => _cache.clear();

/**
 * Pull every feature in `extendedFeatures` despite the server-side
 * MAX_VIZ_FEATURES cap (currently 9 per dev-0.7.26 backend).
 *
 * Strategy: keep firing the endpoint with the remaining (not-yet-received)
 * feature list as the `features` body param until either:
 *   - `remaining` is empty (we got them all)
 *   - the server returns 0 new features (no progress; defensive break)
 *   - we hit MAX_ITERATIONS rounds (defensive break against pathological
 *     backend behavior — should never fire in practice since each round
 *     should peel off >=1 feature)
 *
 * Each fetch uses the existing 5-min request cache in fetchFeatureDistribution,
 * so re-toggling the same expanded view is free after the first pass.
 *
 * Returns a merged response object whose top-level metadata
 * (sites / sample_features_principle / extended_features / limits /
 * warnings) is taken from the FIRST round-trip's response, with a
 * features[] array that is the de-duplicated union of every round's
 * features[]. Feature ordering preserves the order in which backend
 * returned them.
 *
 * Compatible with backend's future MAX_VIZ_FEATURES bump: when the cap
 * goes from 9 to ~30, the very first iteration returns everything and
 * the loop exits immediately. No FE change needed at that time.
 */
export const fetchAllExtendedFeatures = async (
  baseBody,
  extendedFeatures,
  { signal } = {}
) => {
  // If we don't have a curated extended list, just do the default
  // fetch — caller may have constructed an empty extended_features by
  // accident, defensive fallback.
  if (!Array.isArray(extendedFeatures) || extendedFeatures.length === 0) {
    return fetchFeatureDistribution({ ...baseBody, features: [] }, { signal });
  }

  const collectedFeatures = [];
  const seen = new Set(); // by feature.feature (column name)
  let remaining = [...extendedFeatures];
  let firstRoundTop = null;
  const MAX_ITERATIONS = 8; // 8 × 9 cap = up to 72 features; way over any real dataset

  for (let i = 0; i < MAX_ITERATIONS && remaining.length > 0; i++) {
    const body = { ...baseBody, features: remaining };
    const resp = await fetchFeatureDistribution(body, { signal });
    if (!firstRoundTop) firstRoundTop = resp;
    const respFeatures = Array.isArray(resp.features) ? resp.features : [];
    if (respFeatures.length === 0) break;

    const before = remaining.length;
    for (const f of respFeatures) {
      const name = f && f.feature;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      collectedFeatures.push(f);
    }
    remaining = remaining.filter((f) => !seen.has(f));
    // Safety: if backend returned features but none reduced `remaining`
    // (e.g. all the returned features were duplicates of earlier rounds),
    // we'd loop forever. Break instead.
    if (remaining.length >= before) break;
  }

  return {
    ...(firstRoundTop || {}),
    features: collectedFeatures,
  };
};
